/**
 * Execution Table Component
 * Displays list of executions in a table format
 */

import { useNavigate } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { StatusBadge } from './StatusBadge'
import { ChannelBadge } from './ChannelBadge'
import { PhaseBadge } from './PhaseBadge'
import { ProgressBar } from './ProgressBar'
import type { ExecutionMonitoringResponse } from '../types'

interface ExecutionTableProps {
  executions: ExecutionMonitoringResponse[]
  isLoading?: boolean
  onRefresh?: () => void
}

/**
 * Format duration in human-readable format
 */
function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '—'

  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

/**
 * Format timestamp to relative time
 */
function formatTimeAgo(timestamp: string | null): string {
  if (!timestamp) return '—'

  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)

  if (diffSeconds < 60) return 'Just now'
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`
  return date.toLocaleDateString()
}

/**
 * Truncate execution ID for display
 */
function truncateExecutionId(id: string): string {
  if (id.length <= 20) return id
  // For structured IDs like "2024/12/13/ACC/CAMP_123456", show last part
  const parts = id.split('/')
  if (parts.length > 1) {
    return `.../${parts.slice(-2).join('/')}`
  }
  return `${id.substring(0, 8)}...${id.substring(id.length - 8)}`
}

export function ExecutionTable({ executions, isLoading }: ExecutionTableProps) {
  const navigate = useNavigate()

  const handleRowClick = (executionId: string) => {
    navigate(`/executions/${encodeURIComponent(executionId)}`)
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="ml-3 text-gray-500">Loading executions...</span>
        </div>
      </div>
    )
  }

  if (executions.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="text-center text-gray-500">
          <p className="text-lg font-medium">No executions found</p>
          <p className="text-sm mt-1">There are no active or recent campaign executions.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="font-semibold">Execution ID</TableHead>
            <TableHead className="font-semibold">Campaign</TableHead>
            <TableHead className="font-semibold">Channel</TableHead>
            <TableHead className="font-semibold">Status</TableHead>
            <TableHead className="font-semibold">Phase</TableHead>
            <TableHead className="font-semibold w-40">Progress</TableHead>
            <TableHead className="font-semibold text-right">Audience</TableHead>
            <TableHead className="font-semibold text-right">Delivered</TableHead>
            <TableHead className="font-semibold text-right">Failed</TableHead>
            <TableHead className="font-semibold">Started</TableHead>
            <TableHead className="font-semibold">Duration</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {executions.map((execution, index) => (
            <TableRow
              key={`${execution.executionId}-${index}`}
              className="cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => handleRowClick(execution.executionId)}
            >
              <TableCell className="font-mono text-sm text-blue-600 hover:underline">
                {truncateExecutionId(execution.executionId)}
              </TableCell>
              <TableCell className="font-medium">{execution.campaignName || execution.campaignId}</TableCell>
              <TableCell>
                <ChannelBadge channel={execution.channelType} size="sm" />
              </TableCell>
              <TableCell>
                <StatusBadge status={execution.status} size="sm" />
              </TableCell>
              <TableCell>
                <PhaseBadge phase={execution.currentPhase} size="sm" />
              </TableCell>
              <TableCell>
                <ProgressBar
                  value={execution.overallProgressPct}
                  size="sm"
                  showLabel={true}
                  color={
                    execution.status === 'FAILED'
                      ? 'red'
                      : execution.status === 'COMPLETED'
                        ? 'green'
                        : 'blue'
                  }
                />
              </TableCell>
              <TableCell className="text-right font-mono">
                {execution.audienceCount?.toLocaleString() ?? '—'}
              </TableCell>
              <TableCell className="text-right font-mono text-green-600">
                {execution.deliveredCount?.toLocaleString() ?? '—'}
              </TableCell>
              <TableCell className="text-right font-mono text-red-600">
                {execution.failedCount?.toLocaleString() ?? '—'}
              </TableCell>
              <TableCell className="text-gray-500 text-sm">
                {formatTimeAgo(execution.startedAt)}
              </TableCell>
              <TableCell className="font-mono text-sm">
                {formatDuration(execution.durationMs)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default ExecutionTable
